/**
 * Simple Upsell Slider — cart drawer.
 * Server-rendered by snippets/simple-upsell-slider.liquid. As a custom element,
 * connectedCallback/disconnectedCallback handle Flickity lifecycle naturally when
 * cart-drawer-items is replaced on cart updates.
 */
(function () {
  if (customElements.get('simple-upsell-slider')) return;

  class SimpleUpsellSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-simple-upsell-track]');
      if (!this.track || this.track.children.length === 0) return;

      this.prevBtn = this.querySelector('[data-simple-upsell-prev]');
      this.nextBtn = this.querySelector('[data-simple-upsell-next]');

      this.cleanupBreakpoint = window.SliderSettings.onBreakpointChange(() => {
        this.destroySlider();
        this.createSlider();
      });

      if (typeof Flickity !== 'undefined') {
        this.createSlider();
      } else {
        this.onWindowLoad = () => this.createSlider();
        window.addEventListener('load', this.onWindowLoad, { once: true });
      }
    }

    createSlider() {
      if (!this.isConnected) return;

      // Cart-drawer upsell uses custom prev/next buttons + no dots regardless of
      // theme settings, but respects swipe/drag, transition, and step-size.
      const upsellOpts = window.getFlickityOptions(this, { imagesLoaded: true }, {
        prevNextButtons: false,
        pageDots: false,
      });
      this.flkty = new Flickity(this.track, upsellOpts);

      // Flickity recomputes slides[].target on every resize and after a scroll
      // interaction settles — both wipe our padLastSlide() bump. Re-apply it
      // after each so the right-edge gap is persistent, not one-shot.
      this.flkty.on('resize', () => this.padLastSlide());
      this.flkty.on('settle', () => this.padLastSlide());

      this.wireCustomNav();
      this.equalizeCardHeights();

      // rAF-debounced re-equalize — coalesces a burst of mutations or size
      // deltas into one measure-and-layout per frame.
      this.scheduleEqualize = () => {
        if (this.equalizeRafId) cancelAnimationFrame(this.equalizeRafId);
        this.equalizeRafId = requestAnimationFrame(() => this.equalizeCardHeights());
      };

      if (typeof window.datoraUpdatePrices === 'function') {
        window.datoraUpdatePrices(this);
      }

      if (typeof MutationObserver !== 'undefined') {
        this.contentObserver = new MutationObserver(() => this.scheduleEqualize());
        this.contentObserver.observe(this.track, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
      }

      this.onResize = () => this.scheduleEqualize();
      window.addEventListener('resize', this.onResize);
    }

    equalizeCardHeights() {
      const cards = this.track.querySelectorAll('.simple-upsell__card');
      if (cards.length === 0) return;
      this.contentObserver?.disconnect();
      cards.forEach((c) => (c.style.height = ''));
      let maxH = 0;
      cards.forEach((c) => {
        if (c.offsetHeight > maxH) maxH = c.offsetHeight;
      });
      if (maxH > 0) cards.forEach((c) => (c.style.height = maxH + 'px'));
      this.flkty?.resize();
      if (this.contentObserver) {
        this.contentObserver.observe(this.track, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
      }
    }

    wireCustomNav() {
      if (!this.prevBtn || !this.nextBtn) return;

      this.prevBtn.addEventListener('click', () => this.flkty?.previous());
      this.nextBtn.addEventListener('click', () => this.flkty?.next());

      const updateDisabled = () => {
        if (!this.flkty) return;
        const atStart = this.flkty.selectedIndex === 0;
        const atEnd = this.flkty.selectedIndex >= this.flkty.slides.length - 1;
        this.prevBtn.disabled = atStart;
        this.nextBtn.disabled = atEnd;
      };
      updateDisabled();
      this.flkty.on('select', updateDisabled);
    }

    padLastSlide() {
      if (!this.flkty || !this.flkty.slides || this.flkty.slides.length < 2) return;
      // Right-side gap at scroll end, sourced from the container's left padding
      // so the visual stays symmetric with the left side. Flickity's
      // `contain: true` doesn't account for trailing gutter AND it recomputes
      // slides[].target on every resize/settle — so we re-apply here from the
      // 'resize' and 'settle' listeners. Idempotent: if we already padded this
      // slide, subtract the previous pad before re-adding so the gap doesn't
      // accumulate. Identity-check because Flickity may rebuild slide objects.
      const paddingLeft = parseFloat(getComputedStyle(this).paddingLeft) || 24;
      const last = this.flkty.slides[this.flkty.slides.length - 1];
      if (this.paddedLastSlide === last && this.lastSlidePad) {
        last.target -= this.lastSlidePad;
      }
      last.target += paddingLeft;
      this.paddedLastSlide = last;
      this.lastSlidePad = paddingLeft;
    }

    destroySlider() {
      if (this.onResize) {
        window.removeEventListener('resize', this.onResize);
        this.onResize = null;
      }
      if (this.contentObserver) {
        this.contentObserver.disconnect();
        this.contentObserver = null;
      }
      if (this.equalizeRafId) {
        cancelAnimationFrame(this.equalizeRafId);
        this.equalizeRafId = null;
      }
      this.flkty?.destroy();
      this.flkty = null;
    }

    disconnectedCallback() {
      if (this.onWindowLoad) {
        window.removeEventListener('load', this.onWindowLoad);
        this.onWindowLoad = null;
      }
      this.cleanupBreakpoint?.();
      this.destroySlider();
    }
  }

  customElements.define('simple-upsell-slider', SimpleUpsellSlider);
})();
